import { useCallback, useEffect, useMemo, useState } from "react";
import MainScreen from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/MainScreen";
import LoadingModal from "app/pages/digital-suite/home-page/components/Rewards/LoadingModal/LoadingModal";
import InvitationStatusScreen from "app/pages/digital-suite/home-page/components/Rewards/screens/InvitationStatusScreen/InvitationStatusScreen";
import { BasicModal } from "@similarweb/ui-components";
import type { IInvitation, IPackage } from "app/pages/digital-suite/home-page/components/Rewards/utils/types";
import { EModalScreen } from "app/pages/digital-suite/home-page/components/Rewards/utils/types";
import {
    EVENTS_CATEGORY,
    INVITATIONS_PER_PAGE,
    MODAL_WIDTH,
    MODAL_Z_INDEX,
    PADDING_MAIN_SCREEN,
    PADDING_STATUS_SCREEN,
    PADDING_STATUS_SCREEN_WITH_PAGINATION,
} from "app/pages/digital-suite/home-page/components/Rewards/utils/consts";
import { allTrackers } from "app/services/track/track";
import ModalControls from "app/pages/digital-suite/home-page/components/Rewards/ModalControls/ModalControls";

interface IRewardsModalProps {
    isOpen: boolean;
    onClose: VoidFunction;
    invitations: IInvitation[];
    packages: IPackage[];
}

const RewardsModal = ({ isOpen, onClose, invitations, packages }: IRewardsModalProps) => {
    const [invitationsList, setInvitationsList] = useState<IInvitation[]>([]);
    const [isModalLoading, setIsModalLoading] = useState(true);
    const [currentScreen, setCurrentScreen] = useState<EModalScreen>(EModalScreen.MAIN_SCREEN);

    const invitationCount = invitationsList?.length || 0;
    const activeInvitationCount = useMemo(
        () => invitationsList.filter((invitation) => invitation.Status === "active").length || 0,
        [invitationsList],
    );
    const invitationStatusEventName = `Invitations status/Active: ${activeInvitationCount} Pending: ${invitationCount - activeInvitationCount}`;

    const isInvitationsStatusScreen = currentScreen === EModalScreen.INVITATION_STATUS_SCREEN;
    const hasPagination = invitationCount > INVITATIONS_PER_PAGE;

    useEffect(() => {
        if (isOpen) {
            allTrackers.trackEvent(EVENTS_CATEGORY, "open", `Referrals status/${invitationCount}`);
        }
    }, [isOpen]);

    useEffect(() => {
        if (invitations) {
            setInvitationsList(invitations);
        }
    }, [invitations]);

    useEffect(() => {
        setIsModalLoading(!invitations || !packages);
    }, [invitations, packages]);

    const navigateTo = useCallback((screen: EModalScreen) => {
        setCurrentScreen(screen);
    }, []);

    const handleCloseModal = () => {
        let eventName = `Referrals status/${invitationCount}`;
        if (currentScreen === EModalScreen.INVITATION_STATUS_SCREEN) {
            eventName = invitationStatusEventName;
        }

        allTrackers.trackEvent(EVENTS_CATEGORY, "close", eventName);
        onClose();
    };

    const handleOpenStatusScreen = () => {
        allTrackers.trackEvent(EVENTS_CATEGORY, "click", "Invitations status");
        navigateTo(EModalScreen.INVITATION_STATUS_SCREEN);
    };

    const handleBackToMainScreen = () => {
        allTrackers.trackEvent(EVENTS_CATEGORY, "back", invitationStatusEventName);
        navigateTo(EModalScreen.MAIN_SCREEN);
    };

    const MainScreenComponent = useMemo(
        () => (
            <MainScreen
                invitationsList={invitationsList}
                setInvitationsList={setInvitationsList}
                onOpenStatusScreen={handleOpenStatusScreen}
                invitationCount={invitationCount}
                packages={packages}
            />
        ),
        [invitationsList, invitationCount, packages],
    );

    const InvitationScreenComponent = useMemo(
        () => (
            <InvitationStatusScreen
                onBackClick={handleBackToMainScreen}
                invitationsList={invitationsList}
                invitationCount={invitationCount}
                hasPagination={hasPagination}
            />
        ),
        [invitationsList, invitationCount, hasPagination],
    );

    const modalPadding = useMemo(() => {
        if (isInvitationsStatusScreen) {
            return hasPagination ? PADDING_STATUS_SCREEN_WITH_PAGINATION : PADDING_STATUS_SCREEN;
        }
        return PADDING_MAIN_SCREEN;
    }, [isInvitationsStatusScreen, hasPagination]);

    const modalCustomStyles = useMemo(
        () => ({
            content: {
                width: MODAL_WIDTH,
                padding: modalPadding,
            },
            overlay: {
                zIndex: MODAL_Z_INDEX,
            },
        }),
        [modalPadding],
    );

    const renderScreen = () => (isInvitationsStatusScreen ? InvitationScreenComponent : MainScreenComponent);

    return (
        <BasicModal
            isOpen={isOpen}
            closeIconConfig={{ show: false }}
            customStyles={modalCustomStyles}
        >
            <ModalControls onCloseModal={handleCloseModal} />
            {isModalLoading ? <LoadingModal /> : renderScreen()}
        </BasicModal>
    );
};

export default RewardsModal;
